import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { adminAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Flame, Mail, Lock, User, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

export default function Register() {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [siteSettings, setSiteSettings] = useState({});
  const { register } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    adminAPI.getSiteSettings().then(res => setSiteSettings(res.data || {})).catch(() => {});
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!name || !email || !password || !confirmPassword) {
      toast.error('Vui lòng điền đầy đủ thông tin');
      return;
    }

    if (password !== confirmPassword) {
      toast.error('Mật khẩu xác nhận không khớp');
      return;
    }

    if (password.length < 6) {
      toast.error('Mật khẩu phải có ít nhất 6 ký tự');
      return;
    }

    setLoading(true);
    try {
      const response = await register(email, password, name);
      toast.success(response?.message || 'Đăng ký thành công! Vui lòng chờ Admin duyệt tài khoản.');
      navigate('/login');
    } catch (error) {
      const message = error.response?.data?.detail || 'Đăng ký thất bại';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F0F2F5] flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8 animate-fadeIn">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-[#1877F2] to-[#166FE5] rounded-2xl mb-4 shadow-lg">
            <Flame size={32} className="text-white" />
          </div>
          <h1 className="text-2xl font-bold text-[#1C1E21]">{siteSettings.site_name || 'Hotface Analytics'}</h1>
          <p className="text-[#606770] mt-1">{siteSettings.site_subname || 'Phân tích tương tác Facebook'}</p>
        </div>

        {/* Register Card */}
        <Card className="border-0 shadow-xl animate-fadeIn stagger-1" data-testid="register-card">
          <CardHeader className="text-center pb-4">
            <CardTitle className="text-xl font-semibold">Tạo tài khoản</CardTitle>
            <CardDescription>
              Đăng ký để bắt đầu phân tích Facebook
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="name">Họ và tên</Label>
                <div className="relative">
                  <User size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8C939D]" />
                  <Input
                    id="name"
                    type="text"
                    placeholder="Nguyễn Văn A"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="pl-10 h-11"
                    data-testid="name-input"
                    disabled={loading}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <div className="relative">
                  <Mail size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8C939D]" />
                  <Input
                    id="email"
                    type="email"
                    placeholder="email@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="pl-10 h-11"
                    data-testid="email-input"
                    disabled={loading}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="password">Mật khẩu</Label>
                <div className="relative">
                  <Lock size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8C939D]" />
                  <Input
                    id="password"
                    type="password"
                    placeholder="Ít nhất 6 ký tự"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="pl-10 h-11"
                    data-testid="password-input"
                    disabled={loading}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirmPassword">Xác nhận mật khẩu</Label>
                <div className="relative">
                  <Lock size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8C939D]" />
                  <Input
                    id="confirmPassword"
                    type="password"
                    placeholder="Nhập lại mật khẩu"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="pl-10 h-11"
                    data-testid="confirm-password-input"
                    disabled={loading}
                  />
                </div>
              </div>

              <Button 
                type="submit" 
                className="w-full h-11 bg-[#1877F2] hover:bg-[#166FE5] font-semibold"
                disabled={loading}
                data-testid="register-btn"
              >
                {loading ? (
                  <>
                    <Loader2 size={18} className="mr-2 animate-spin" />
                    Đang đăng ký...
                  </>
                ) : (
                  'Đăng ký'
                )}
              </Button>
            </form>

            <div className="mt-6 text-center">
              <p className="text-sm text-[#606770]">
                Đã có tài khoản?{' '}
                <Link 
                  to="/login" 
                  className="text-[#1877F2] font-semibold hover:underline"
                  data-testid="login-link"
                >
                  Đăng nhập
                </Link>
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Features */}
        <div className="mt-6 grid grid-cols-3 gap-4 text-center animate-fadeIn stagger-2">
          <div className="p-3">
            <div className="text-2xl font-bold text-[#1877F2]">100</div>
            <div className="text-xs text-[#606770]">Credits miễn phí</div>
          </div>
          <div className="p-3">
            <div className="text-2xl font-bold text-[#31A24C]">Cao</div>
            <div className="text-xs text-[#606770]">Độ chính xác</div>
          </div>
          <div className="p-3">
            <div className="text-2xl font-bold text-[#F5C33B]">24/7</div>
            <div className="text-xs text-[#606770]">Hỗ trợ</div>
          </div>
        </div>

        {/* Footer */}
        <p className="text-center text-xs text-[#8C939D] mt-6">
          {siteSettings.footer_copyright || 'Hotface Analytics \u00A9 2024. All rights reserved.'}
        </p>
      </div>
    </div>
  );
}
